package com.siriusxm.pia.components

import androidx.compose.runtime.*
import com.siriusxm.pia.SXMUI
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Text

/**
 * Attributes for the dialog overlay
 */
interface DialogOverlayAttributes {
    /**
     * The main title of the dialog
     */
    var title: String?

    /**
     * Called when closing the dialog
     */
    fun onClose(cb: suspend (Boolean) -> Unit)

    /**
     * Install a content rendering function
     */
    fun content(block: @Composable () -> Unit)

    /**
     * Update the label for the default close button. Defaults to "Close".
     */
    var closeActionTitle: String

    /**
     * Install a callback to save the content of the dialog.
     */
    fun save(block: () -> Unit)

    /**
     * Install an action button.
     *
     * The [config] block may read Compose state, and so may the code that computes the values it
     * is given. Either way the button follows that state - see the note on reactivity in the
     * [dialogOverlay] documentation.
     */
    fun action(config: ButtonConfiguration.() -> Unit)
}

/**
 * Render a dialog overlay: a modal card centered over a dimmed backdrop, with a title bar and a
 * close affordance, the caller's content, and a footer holding the Close button, any action
 * buttons and the Save button.
 *
 * The dialog is configured by [attrs], which runs as part of this composable. Because [attrs] is
 * itself composable, Compose gives it its own recompose scope, so a state change read in its body
 * would normally re-run only that lambda - leaving the already-rendered footer and title behind
 * with stale values. That was UCA-602: an action button's `enabled` flag never followed the form
 * state.
 *
 * To make configuration reactive, every mutator below reports back through [markConfigChanged].
 * While this composable is running [attrs] itself (`configuring`) those reports are ignored. Once
 * it has finished, a later call can only come from an isolated re-run of [attrs], so the mutator
 * invalidates this composable. The next frame then re-runs the whole dialog, rebuilding the title,
 * content, callbacks and action list from scratch. Writes made to the superseded configuration are
 * discarded, so repeatedly re-running [attrs] cannot accumulate duplicate action buttons.
 *
 * The practical contract for callers: a value used in [attrs] may be computed anywhere - at the top
 * of the calling composable, in the [attrs] body, or inside an action's config block - and the
 * dialog will reflect it. The one limit: a re-run of [attrs] is only noticed if it calls at least
 * one mutator, so configure something unconditionally (a title, [DialogOverlayAttributes.content]
 * or [DialogOverlayAttributes.onClose]) rather than making the entire [attrs] body conditional.
 */
@Composable
fun dialogOverlay(attrs: @Composable DialogOverlayAttributes.() -> Unit = {}) {
    var onClose: suspend (Boolean) -> Unit = {}
    var content: @Composable () -> Unit = {}
    var save: (() -> Unit)? = null
    var closeTitle = "Close"
    val actions = mutableListOf<ButtonConfiguration.() -> Unit>()

    val recomposeScope = currentRecomposeScope

    // True only while this composable is running the attrs lambda as part of its own composition.
    // Any configuration change reported after that comes from an isolated re-run of attrs, and has
    // to be promoted to a recomposition of the whole dialog.
    var configuring = true

    val attributes = object : DialogOverlayAttributes {
        private fun markConfigChanged() {
            if (!configuring) recomposeScope.invalidate()
        }

        override var title: String? = null
            set(value) {
                field = value
                markConfigChanged()
            }

        override var closeActionTitle: String
            get() = closeTitle
            set(value) {
                closeTitle = value
                markConfigChanged()
            }

        override fun onClose(cb: suspend (Boolean) -> Unit) {
            onClose = cb
            markConfigChanged()
        }

        override fun content(block: @Composable () -> Unit) {
            content = block
            markConfigChanged()
        }

        override fun save(block: () -> Unit) {
            save = block
            markConfigChanged()
        }

        override fun action(config: ButtonConfiguration.() -> Unit) {
            actions += config
            markConfigChanged()
        }
    }
    attributes.attrs()
    configuring = false

    val closeDialog by rememberUpdatedState(onClose)
    val coroutineScope = rememberCoroutineScope()

    Div({
        style {
            display(DisplayStyle.Block)
            position(Position.Fixed)
            top(0.px)
            bottom(0.px)
            right(0.px)
            left(0.px)
            backgroundColor(rgba(35, 43, 55, 0.7))
            property("z-index", "100")
            overflow("auto")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Block)
                width(100.percent)
                maxWidth(820.px)
                marginTop(50.px)
                marginBottom(50.px)
                property("margin-left", "auto")
                property("margin-right", "auto")
                backgroundColor(SXMUI.containerContentBackgroundColor.value())
                border(1.px, LineStyle.Solid, SXMUI.tabsBorder.value())
                roundedBox()
                overflow("hidden")
            }
        }) {
            Div({
                style {
                    padding(16.px, 20.px)
                    position(Position.Relative)
                    backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
                    border(0.px, 0.px, 1.px, 0.px, SXMUI.defaultDivider.value())
                }
            }) {
                attributes.title?.let { title ->
                    H2({
                        style {
                            margin(0.px)
                        }
                    }) {
                        Text(title)
                    }
                }

                Button({
                    attr("type", "button")
                    attr("aria-label", "Close dialog")
                    title("Close dialog")
                    style {
                        position(Position.Absolute)
                        top(4.px)
                        right(4.px)
                        width(32.px)
                        height(32.px)
                        padding(0.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        backgroundColor(Color.transparent)
                        color(SXMUI.textColor.value())
                        border(0.px)
                        cursor("pointer")
                    }
                    onClick {
                        coroutineScope.launch { closeDialog(false) }
                    }
                }) {
                    svgIcon(SVGIcons.xMark) { size = 20.px }
                }
            }

            Div {
                content()
            }

            Div({
                style {
                    borderTop()
                    padding(12.px, 20.px)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    gap(10.px)
                    backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
                }
            }) {
                button(closeTitle, false) {
                    closeDialog(false)
                }

                actions.forEach {
                    button {
                        it()
                    }
                }

                save?.let { save ->
                    button("Save", true) {
                        save()
                        closeDialog(false)
                    }
                }
            }
        }
    }
}

/**
 * Display an icon that, when clicked, displays a dialog.
 */
@Composable
fun dialogIcon(icon: String = "settings", config: @Composable DialogOverlayAttributes.() -> Unit) {
    var showDialog by remember { mutableStateOf(false) }

    icon(icon) {
        action {
            showDialog = true
        }
    }
    if (showDialog) {
        dialogOverlay {
            config.invoke(this)
            onClose {
                showDialog = false
            }
        }
    }
}


/**
 * Display a confirmation dialog that requires the viewer to enter some confirmation text.
 * @param cb if called with true, the action should be completed, otherwise, just close the dialog.
 */
@Composable
fun confirmationDialog(
    title: String, message: String,
    action: String,
    confirmationText: String? = null,
    cb: suspend (Boolean) -> Unit
) {
    confirmationDialogWithContent(
        title = title,
        action = action,
        confirmationText = confirmationText,
        cb = cb
    ) { _, _ ->
        Text(message)
    }
}

/**
 * Display a confirmation dialog with custom content that requires the viewer to enter some confirmation text.
 * @param cb if called with true, the action should be completed, otherwise, just close the dialog.
 */
@Composable
fun confirmationDialogWithContent(
    title: String,
    action: String,
    confirmationText: String? = null,
    confirmationInstruction: String? = "Type '${confirmationText}' to confirm",
    cb: suspend (Boolean) -> Unit,
    content: @Composable (String, (String) -> Unit) -> Unit
) {
    var confirmation by remember { mutableStateOf("") }
    dialogOverlay {
        this.title = title
        closeActionTitle = "Cancel"
        this.action {
            primary = true
            this.title = action
            enabled = confirmationText == null || confirmation == confirmationText
            showProgressOnAction = true
            action {
                cb(true)
            }
        }
        this.content {
            Div({
                style {
                    padding(10.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(10.px)
                }
            }) {
                content(confirmation) { confirmation = it }
                if (confirmationText != null) {
                    dialogField(confirmationInstruction) {
                        simpleTextField(confirmation) {
                            confirmation = it.orEmpty()
                        }
                    }
                }
            }
        }
        onClose {
            cb(false)
        }
    }
}
